import React, { type CSSProperties, type RefObject } from 'react';
import { debounce } from 'lodash';
import findParent from '../../util/findParent';
import shallowCompare from '../../util/shallowCompare';
import classnames from 'classnames';
import styles from './Anchor.module.css';
import editorStyles from '../AhaEditorV2.module.css';

interface AnchorProps {
  toolbarOffset?: number;
  className?: string;
  children: React.ReactNode;
  fullScreen: boolean;
  focused: boolean;
}

interface AnchorState {
  style: CSSProperties;
  computedTop: number;
  computedLeft: number;
  computedHeight: number;
  computedWidth: number;
}

export default class Anchor extends React.Component<AnchorProps, AnchorState> {
  state: AnchorState = {
    style: {},
    computedTop: 0,
    computedLeft: 0,
    computedHeight: 0,
    computedWidth: 0,
  };

  placeholderRef: RefObject<HTMLDivElement>;
  anchorRef: RefObject<HTMLDivElement>;
  animationFrameRequest: number | null = null;
  container: HTMLElement | null = null;
  scrollContainer: HTMLElement | null = null;

  constructor(props: AnchorProps) {
    super(props);
    this.placeholderRef = React.createRef();
    this.anchorRef = React.createRef();
  }

  recomputeStyle = () => {
    const node =
      (this.state.style.position
        ? this.placeholderRef.current
        : this.anchorRef.current) || this.anchorRef.current;

    if (node) {
      let rect = node.getBoundingClientRect();

      if (rect.height === 0) {
        rect = (node.parentElement as HTMLElement).getBoundingClientRect();
      }

      const top = this.getAbsoluteCoordinate(rect.top);

      this.setState(
        {
          computedTop: top,
          computedLeft: rect.left,
          computedHeight: rect.height,
          computedWidth: rect.width,
        },
        () => {
          this.recalculateSticky();
        }
      );
    } else {
      this.recalculateSticky();
    }
  };

  debouncedRecomputeStyle = debounce(this.recomputeStyle, 100);

  getAbsoluteCoordinate(coordinate: number) {
    let absoluteCoordinate = coordinate + window.pageYOffset;
    if (this.scrollContainer) {
      absoluteCoordinate += this.scrollContainer.scrollTop;
    }
    return absoluteCoordinate;
  }

  checkSticky = () => {
    if (this.animationFrameRequest) return;

    this.animationFrameRequest = window.requestAnimationFrame(() => {
      this.animationFrameRequest = null;

      this.recalculateSticky();
    });
  };

  private recalculateSticky() {
    const scrollContainerOriginalTop = this.scrollContainer
      ? this.scrollContainer.getBoundingClientRect().top
      : 0;
    const { toolbarOffset = 0 } = this.props;
    const top =
      this.getAbsoluteCoordinate(scrollContainerOriginalTop) + toolbarOffset;

    let bottom;

    if (this.container) {
      const containerRect = this.container.getBoundingClientRect();
      // If the container is hidden, its height will be zero, which
      // means we'd always float the toolbar, which makes it hard to
      // calculate the size when it's shown. It's better to treat a
      // hidden container as a nonexistent container.
      if (containerRect.height > 0) {
        bottom = this.getAbsoluteCoordinate(containerRect.bottom);
      }
    }

    if (
      this.state.computedHeight > 0 &&
      bottom !== undefined &&
      bottom <= top + this.state.computedHeight
    ) {
      this.setState({
        style: {
          position: 'absolute',
          bottom: 0,
          zIndex: 1, // move above Bootstrap checklists
        },
      });
    } else if (this.state.computedHeight > 0 && top > this.state.computedTop) {
      const { toolbarOffset = 0 } = this.props;
      this.setState({
        style: {
          position: 'fixed',
          top: toolbarOffset,
          left: 0,
          width: this.state.computedWidth,
          zIndex: 1, // move above Bootstrap checklists
          transform: `translate3d(${this.state.computedLeft}px, ${scrollContainerOriginalTop}px, 0px)`,
        },
      });
    } else if (this.state.style.position) {
      this.setState({
        style: {},
      });
    }
  }

  findScrollContainer = findParent((el) => {
    const computedStyle = window.getComputedStyle(el);
    const overflow = computedStyle?.overflow || '';

    // Handle auto/scroll being defined in any of overflow/overflow-x/overflow-y
    return overflow.includes('auto') || overflow.includes('scroll');
  });

  findRelativeContainer = findParent((el) => {
    const computedStyle = window.getComputedStyle(el);
    const position = computedStyle?.position;
    return position === 'relative';
  });

  componentDidMount() {
    this.animationFrameRequest = null;
    this.scrollContainer = this.anchorRef.current
      ? this.findScrollContainer(this.anchorRef.current)
      : null;
    this.container = this.anchorRef.current
      ? this.findRelativeContainer(this.anchorRef.current)
      : null;

    (this.scrollContainer ?? window).addEventListener(
      'scroll',
      this.checkSticky
    );

    window.addEventListener('resize', this.recomputeStyle);
    this.recomputeStyle();
  }

  componentWillUnmount() {
    if (this.animationFrameRequest) {
      window.cancelAnimationFrame(this.animationFrameRequest);
    }
    this.debouncedRecomputeStyle.cancel();
    (this.scrollContainer ?? window).removeEventListener(
      'scroll',
      this.checkSticky
    );
    window.removeEventListener('resize', this.recomputeStyle);
  }

  componentDidUpdate(prevProps: AnchorProps, prevState: AnchorState) {
    // Changes to us and our children, like adding or showing child
    // elements, can affect our original dimensions.
    if (
      this.props !== prevProps ||
      !shallowCompare(this.state, prevState, ['style'])
    ) {
      this.debouncedRecomputeStyle();
    }
  }

  shouldComponentUpdate(nextProps: AnchorProps, nextState: AnchorState) {
    if (this.props !== nextProps) return true;
    if (!shallowCompare(this.state, nextState, ['style'])) return true;
    if (!shallowCompare(this.state.style, nextState.style)) return true;
    return false;
  }

  render() {
    let style: CSSProperties = {};
    let placeholder: React.ReactNode = null;

    if (!this.props.fullScreen && this.props.focused) {
      style.willChange = 'transform';

      if (this.state.style.position) {
        style = {
          ...style,
          ...this.state.style,
        };

        const placeholderContainerStyle = {
          height: this.state.computedHeight,
        };

        placeholder = (
          <div
            className={classnames(
              styles.anchorPlaceholder,
              this.props.className
            )}
            ref={this.placeholderRef}
            style={placeholderContainerStyle}
          />
        );
      }
    }

    return (
      <div className={editorStyles.anchor}>
        <div
          ref={this.anchorRef}
          style={style}
          className={classnames(styles.anchor, this.props.className, {
            [styles.fullScreen]: this.props.fullScreen,
          })}
        >
          {this.props.children}
        </div>
        {placeholder}
      </div>
    );
  }
}
